/**
 * Display labels for the image-output enums, and the aspect ratios the UI lays out with.
 *
 * **Only the presentation half is here.** `lib/constants/imageSizes.ts` and
 * `lib/constants/printSizes.ts` carried both the labels and the pixel arithmetic; the
 * arithmetic is the backend's now (`app/domain/constants/`), and duplicating it would be a
 * second source of truth for how big a print is. What cannot live there is what a human should
 * read on screen, which is what this module is.
 *
 * The keys are typed from the generated schema rather than re-declared, so a ratio or print
 * size added to the backend breaks this file at compile time instead of silently rendering as
 * its raw value.
 */

import type { components } from "@/api/schema";

type Schemas = components["schemas"];

export type Ratio = Schemas["Ratio"];
export type PrintSizeKey = Schemas["PrintSizeKey"];
export type GenerationMode = Schemas["GenerationMode"];

/**
 * CSS `aspect-ratio` per ratio, so a thumbnail reserves the right space before it loads.
 * Without it every card in a grid reflows as its image arrives.
 */
export const RATIO_ASPECT_CSS: Record<Ratio, string> = {
  portrait: "2 / 3",
  landscape: "3 / 2",
  square: "1 / 1",
};

export const RATIO_LABELS: Record<Ratio, string> = {
  portrait: "Portrait",
  landscape: "Landscape",
  square: "Square",
};

export const PRINT_SIZE_LABELS: Record<PrintSizeKey, string> = {
  "4x6": "4 x 6 in",
  "5x7": "5 x 7 in",
  "8x10": "8 x 10 in",
  a4: "A4",
  a3: "A3",
};

export const MODE_LABELS: Record<GenerationMode, string> = {
  describe: "Described",
  edit: "Edited",
};

/**
 * Monthly generation tiers an admin can grant, from the Usage page.
 *
 * `null` is not a tier — it is the meaning every client starts with, "no monthly cap" — so
 * granting a tier is opt-in per client rather than a quota that appears on every existing
 * account the day the feature ships. The backend's `MONTHLY_GENERATION_TIERS` is the
 * authority; these are the same values for the picker.
 */
export const GENERATION_TIER_OPTIONS: readonly number[] = [5, 20, 50, 100, 250];
